import React, { useCallback, useEffect, useState } from "react";
import KlubbVelger, { getKlubbId } from "./KlubbVelger";
import { isLeder } from "../../employeeAccess";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ROLE_LABEL = { TRENER: "Trener", INNKJOPSANSVARLIG: "Innkjøpsansvarlig" };

function PersonPicker({ value, onChange }) {
  const [mode, setMode] = useState("eksisterende"); // "eksisterende" | "ny"
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/klubbsystem/people/search?q=${encodeURIComponent(q)}`);
        setResults(res.ok ? await res.json() : []);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Advarsel mot å opprette en duplikat: e-post-unikhet fanges opp av backend først når man
  // faktisk lagrer, men et ulikt stavet/annet navn på samme person ville aldri truffet den
  // sjekken — søk derfor på navnet mens man skriver «Ny person», samme terskel (2 tegn) som
  // det vanlige personsøket.
  useEffect(() => {
    if (mode !== "ny") {
      setSimilar([]);
      return;
    }
    const q = (value.name || "").trim();
    if (q.length < 2) {
      setSimilar([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/klubbsystem/people/search?q=${encodeURIComponent(q)}`);
        setSimilar(res.ok ? await res.json() : []);
      } catch {
        setSimilar([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [mode, value.name]);

  const selectPerson = (person) => {
    onChange({ mode: "eksisterende", personId: person._id, label: person.name });
    setMode("eksisterende");
    setQuery("");
    setResults([]);
  };

  const switchMode = (next) => {
    setMode(next);
    onChange(next === "ny" ? { mode: "ny", name: "", email: "", phone: "" } : { mode: "eksisterende", personId: "", label: "" });
  };

  return (
    <div>
      <div className="flex gap-1 mb-1.5">
        <button type="button" onClick={() => switchMode("eksisterende")} className={`px-2.5 py-1 rounded-md text-xs font-medium ${mode === "eksisterende" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600"}`}>
          Eksisterende person
        </button>
        <button type="button" onClick={() => switchMode("ny")} className={`px-2.5 py-1 rounded-md text-xs font-medium ${mode === "ny" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600"}`}>
          Ny person
        </button>
      </div>

      {mode === "eksisterende" ? (
        <div className="relative">
          {value.personId ? (
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50">
              <span className="font-medium text-gray-900">{value.label}</span>
              <button type="button" onClick={() => onChange({ mode: "eksisterende", personId: "", label: "" })} className="text-gray-400 hover:text-red-600 text-xs ml-auto">
                Bytt
              </button>
            </div>
          ) : (
            <>
              <input
                placeholder="Søk på navn …"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {(searching || results.length > 0) && query.trim().length >= 2 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                  {searching && <p className="px-3 py-2 text-xs text-gray-400">Søker …</p>}
                  {!searching && results.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Ingen treff.</p>}
                  {results.map((p) => (
                    <button
                      type="button"
                      key={p._id}
                      onClick={() => selectPerson(p)}
                      className="block w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                    >
                      <span className="font-medium text-gray-900">{p.name}</span>
                      {p.roles.length > 0 && (
                        <span className="text-gray-400 text-xs ml-2">
                          ({p.roles.map((r) => `${ROLE_LABEL[r.role] || r.role}${r.team ? ` · ${r.team}` : ""}`).join(", ")})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-2">
            <input placeholder="Navn" className="flex-1 min-w-[140px] border border-gray-300 rounded-lg px-3 py-2 text-sm" value={value.name || ""} onChange={(e) => onChange({ ...value, name: e.target.value })} />
            <input placeholder="E-post" type="email" className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm" value={value.email || ""} onChange={(e) => onChange({ ...value, email: e.target.value })} />
            <input placeholder="Telefon (valgfritt)" className="w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm" value={value.phone || ""} onChange={(e) => onChange({ ...value, phone: e.target.value })} />
          </div>
          {similar.length > 0 && (
            <div className="mt-2 border border-amber-200 bg-amber-50 rounded-lg p-2">
              <p className="text-xs text-amber-800 font-medium mb-1">Fant person(er) med lignende navn — er dette samme person?</p>
              {similar.map((p) => (
                <button
                  type="button"
                  key={p._id}
                  onClick={() => selectPerson(p)}
                  className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-amber-100 text-sm"
                >
                  <span className="font-medium text-gray-900">{p.name}</span>
                  <span className="text-gray-500 text-xs ml-2">{p.email}</span>
                  {p.roles.length > 0 && (
                    <span className="text-gray-400 text-xs ml-2">
                      ({p.roles.map((r) => `${ROLE_LABEL[r.role] || r.role}${r.team ? ` · ${r.team}` : ""}`).join(", ")})
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EMPTY_PERSON = { mode: "eksisterende", personId: "", label: "" };

// Nye klubber har ingen lag ved opprettelse — en leder må kunne legge til laget selv, i riktig
// gren, i stedet for å måtte be en systemadmin gjøre det i klubbsport1 først (§40).
function NewTeamForm({ departments, onCreated }) {
  const [open, setOpen] = useState(false);
  const [departmentId, setDepartmentId] = useState(departments[0]?._id || "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Grenlisten lastes asynkront og kan fortsatt være forrige klubbs (eller tom) idet denne
  // komponenten monteres — uten dette ble et lag av og til forsøkt opprettet i en gren fra feil
  // klubb, som backend korrekt avviste som "Fant ikke gren".
  useEffect(() => {
    if (!departments.some((d) => d._id === departmentId)) {
      setDepartmentId(departments[0]?._id || "");
    }
  }, [departments, departmentId]);

  // type="button" + onClick i stedet for et nøstet <form onSubmit> — denne komponenten renders
  // inne i AssignForm sitt eget <form>, og nøstede <form>-elementer er ugyldig HTML som gjør at
  // innerste skjemaets submit ikke kan stoles på (oppdaget ved at "Opprett" ikke gjorde noe).
  const submit = async () => {
    if (!departmentId || !name.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clubId: getKlubbId(), departmentId, name: name.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Kunne ikke opprette lag");
      setName("");
      setOpen(false);
      await onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-indigo-600 hover:text-indigo-500 text-xs font-medium">
        + Nytt lag
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 bg-gray-50 rounded-lg p-2">
      <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm">
        {departments.map((d) => (
          <option key={d._id} value={d._id}>{d.name}</option>
        ))}
      </select>
      <input
        placeholder="Lagnavn, f.eks. G2016"
        className="flex-1 min-w-[120px] border border-gray-300 rounded-md px-2 py-1.5 text-sm"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        autoFocus
      />
      <button type="button" onClick={submit} disabled={busy} className="px-2.5 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold disabled:opacity-50">
        {busy ? "…" : "Opprett"}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-xs">Avbryt</button>
      {error && <p className="text-red-600 text-xs w-full">{error}</p>}
    </div>
  );
}

function AssignForm({ teams, departments, onTeamsChanged, onAssigned }) {
  const [role, setRole] = useState("TRENER");
  const [teamIds, setTeamIds] = useState([]);
  const [clubWide, setClubWide] = useState(false);
  const [title, setTitle] = useState("");
  const [person, setPerson] = useState(EMPTY_PERSON);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const changeRole = (nextRole) => {
    setRole(nextRole);
    if (nextRole === "TRENER") setClubWide(false);
  };

  const allSelected = teams.length > 0 && teamIds.length === teams.length;
  const toggleTeam = (id) => setTeamIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleAll = () => setTeamIds(allSelected ? [] : teams.map((t) => t._id));

  const handleTeamCreated = async (team) => {
    await onTeamsChanged();
    setTeamIds((ids) => [...ids, team._id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!clubWide && teamIds.length === 0) return setError(role === "TRENER" ? "Velg minst ett lag" : "Velg minst ett lag, eller kryss av for «Hele klubben»");
    if (person.mode === "eksisterende" && !person.personId) return setError("Velg en person, eller bytt til «Ny person»");
    if (person.mode === "ny" && (!person.name.trim() || !person.email.trim())) return setError("Navn og e-post er påkrevd for en ny person");

    setBusy(true);
    try {
      const personFields = {
        personId: person.mode === "eksisterende" ? person.personId : undefined,
        person: person.mode === "ny" ? { name: person.name, email: person.email, phone: person.phone } : undefined,
      };
      const url = clubWide
        ? `${API_BASE_URL}/klubbsystem/role-assignments`
        : `${API_BASE_URL}/klubbsystem/role-assignments/bulk`;
      const body = clubWide
        ? { clubId: getKlubbId(), role, title, ...personFields }
        : { clubId: getKlubbId(), role, teamIds, title, ...personFields };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke tildele rolle");
      }
      setTitle("");
      setTeamIds([]);
      setClubWide(false);
      setPerson(EMPTY_PERSON);
      onAssigned();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-gray-200 rounded-xl p-5 space-y-3">
      <h2 className="text-sm font-semibold text-gray-900">Legg til trener eller innkjøpsansvarlig</h2>

      <PersonPicker value={person} onChange={setPerson} />

      <div className="flex flex-wrap gap-2 items-start">
        <select value={role} onChange={(e) => changeRole(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="TRENER">Trener</option>
          <option value="INNKJOPSANSVARLIG">Innkjøpsansvarlig</option>
        </select>
        <input placeholder="Tittel (valgfritt), f.eks. Hovedtrener" className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      {role === "INNKJOPSANSVARLIG" && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={clubWide} onChange={(e) => setClubWide(e.target.checked)} />
          Hele klubben (alle lag/grener, også fremtidige)
        </label>
      )}

      {!clubWide && (
        <div>
          <div className="border border-gray-200 rounded-lg max-h-44 overflow-y-auto p-2">
            {teams.length === 0 && <p className="text-xs text-gray-400 px-1 py-1">Ingen lag registrert ennå — opprett ett under.</p>}
            {teams.length > 0 && (
              <label className="flex items-center gap-2 text-sm font-medium border-b border-gray-100 pb-1.5 mb-1.5">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                Alle lag
              </label>
            )}
            {teams.map((t) => (
              <label key={t._id} className="flex items-center gap-2 text-sm py-0.5">
                <input type="checkbox" checked={teamIds.includes(t._id)} onChange={() => toggleTeam(t._id)} />
                {t.name}
                <span className="text-gray-400 text-xs">{t.departmentId?.name}</span>
              </label>
            ))}
          </div>
          <div className="mt-2">
            <NewTeamForm departments={departments} onCreated={handleTeamCreated} />
          </div>
        </div>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <button type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50">
        {busy ? "Lagrer …" : "+ Tildel rolle"}
      </button>
    </form>
  );
}

function AssignmentRow({ assignment, teams, leder, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [teamId, setTeamId] = useState(assignment.teamId?._id || "");
  const [title, setTitle] = useState(assignment.title || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const startEdit = () => {
    setTeamId(assignment.teamId?._id || "");
    setTitle(assignment.title || "");
    setError("");
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/role-assignments/${assignment._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ teamId, title }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke oppdatere");
      }
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Fjerne ${assignment.personId?.name || "personen"} som ${ROLE_LABEL[assignment.role]}?`)) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE_URL}/klubbsystem/role-assignments/${assignment._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "IKKE_AKTIV" }),
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr className="border-t border-gray-100 bg-indigo-50/40">
        <td className="px-4 py-2.5 font-medium text-gray-900">{assignment.personId?.name || "—"}</td>
        <td className="px-4 py-2.5 text-gray-500">{ROLE_LABEL[assignment.role]}</td>
        <td className="px-4 py-2.5">
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm">
            {assignment.role === "INNKJOPSANSVARLIG" && <option value="">Hele klubben</option>}
            {teams.map((t) => (
              <option key={t._id} value={t._id}>{t.name}</option>
            ))}
          </select>
        </td>
        <td className="px-4 py-2.5">
          <input className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tittel" />
        </td>
        <td className="px-4 py-2.5 text-gray-500">{assignment.personId?.email || "—"}</td>
        <td className="px-4 py-2.5 text-right whitespace-nowrap">
          {error && <span className="text-red-600 text-xs mr-2">{error}</span>}
          <button disabled={busy} onClick={save} className="text-indigo-600 hover:text-indigo-500 text-xs font-semibold mr-3">Lagre</button>
          <button disabled={busy} onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600 text-xs font-medium">Avbryt</button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-gray-100">
      <td className="px-4 py-2.5 font-medium text-gray-900">{assignment.personId?.name || "—"}</td>
      <td className="px-4 py-2.5 text-gray-500">{ROLE_LABEL[assignment.role]}{assignment.title ? ` (${assignment.title})` : ""}</td>
      <td className="px-4 py-2.5 text-gray-500">{assignment.teamId?.name || (assignment.role === "INNKJOPSANSVARLIG" ? "Hele klubben" : "—")}</td>
      <td className="px-4 py-2.5 text-gray-500">{assignment.personId?.email || "—"}</td>
      <td className="px-4 py-2.5 text-gray-500">{assignment.personId?.phone || "—"}</td>
      {leder && (
        <td className="px-4 py-2.5 text-right whitespace-nowrap">
          <button disabled={busy} onClick={startEdit} className="text-indigo-600 hover:text-indigo-500 text-xs font-medium mr-3">Endre</button>
          <button disabled={busy} onClick={remove} className="text-gray-400 hover:text-red-600 text-xs font-medium">Fjern</button>
        </td>
      )}
    </tr>
  );
}

export default function Roller() {
  const [assignments, setAssignments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const leder = isLeder();

  const loadTeams = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/klubbsystem/teams?clubId=${getKlubbId()}`);
    if (res.ok) setTeams(await res.json());
  }, []);

  const load = useCallback(async () => {
    try {
      const clubId = getKlubbId();
      const [trenereRes, innkjopRes, teamsRes, departmentsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/klubbsystem/role-assignments?clubId=${clubId}&role=TRENER`),
        fetch(`${API_BASE_URL}/klubbsystem/role-assignments?clubId=${clubId}&role=INNKJOPSANSVARLIG`),
        fetch(`${API_BASE_URL}/klubbsystem/teams?clubId=${clubId}`),
        fetch(`${API_BASE_URL}/klubbsystem/departments?clubId=${clubId}`),
      ]);
      if (!trenereRes.ok || !innkjopRes.ok || !teamsRes.ok) throw new Error("Kunne ikke hente roller fra klubbsport1");
      const [trenere, innkjop, teamsData, departmentsData] = await Promise.all([
        trenereRes.json(),
        innkjopRes.json(),
        teamsRes.json(),
        departmentsRes.ok ? departmentsRes.json() : [],
      ]);
      const combined = [...trenere, ...innkjop].sort(
        (a, b) => (a.teamId?.name || "").localeCompare(b.teamId?.name || "") || (a.personId?.name || "").localeCompare(b.personId?.name || "")
      );
      setAssignments(combined);
      setTeams(teamsData);
      setDepartments(departmentsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mb-3">
        Klubb
      </span>
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Roller</h1>
      <p className="text-gray-500 mb-6">
        Trenere og innkjøpsansvarlige per lag.
        {!leder && " Kun ansatte med lederrolle kan legge til eller endre."}
      </p>

      <KlubbVelger onChange={load} />

      {loading && <p className="text-gray-400 text-sm">Laster …</p>}
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {!loading && !error && (
        assignments.length === 0 ? (
          <p className="text-gray-400 text-sm mb-6">Ingen trenere eller innkjøpsansvarlige registrert ennå.</p>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">Person</th>
                    <th className="text-left px-4 py-2.5">Rolle</th>
                    <th className="text-left px-4 py-2.5">Lag</th>
                    <th className="text-left px-4 py-2.5">E-post</th>
                    <th className="text-left px-4 py-2.5">Telefon</th>
                    {leder && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => (
                    <AssignmentRow key={a._id} assignment={a} teams={teams} leder={leder} onChanged={load} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {leder && !loading && (
        <AssignForm teams={teams} departments={departments} onTeamsChanged={loadTeams} onAssigned={load} />
      )}
    </div>
  );
}
